import type {
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { invariant } from "~/lib/invariant";
import { merge } from "~/lib/std";
import type { Maybe } from "~/types";
import { scheduleQueriesRemoval } from "~/utils";
import { DsmQueryClientContext, useDsmContext } from "../provider";
import type {
  ListUsersRequest,
  MeDataResponse,
  User,
  UserCreateRequest,
  UserDataResponse,
  UserListResponse,
  UserUpdateRequest,
} from "../sdk";
import type { DsmMutationOptions, DsmQueryOptions } from "./utils";
import { useDsmMutation, useDsmQueryClient } from "./utils";

export const userKeys = {
  all: ["users"] as const,
  lists: () => [...userKeys.all, "list"] as const,
  list: (request: ListUsersRequest) => [...userKeys.lists(), request] as const,
  fetches: () => [...userKeys.all, "fetch"] as const,
  fetch: (userId: User["id"] | null) =>
    [...userKeys.fetches(), userId] as const,
  me: () => ["current-user"] as const,
};

export function useUsersQueryOptionsFactory(): (
  request: ListUsersRequest,
) => DsmQueryOptions<UserListResponse> {
  const { userApi } = useDsmContext();

  return (request) => ({
    queryKey: userKeys.list(request),
    queryFn({ signal }) {
      return userApi.listUsers(request, { signal });
    },
    context: DsmQueryClientContext,
  });
}

export function useUserQueryOptionsFactory(): (
  userId: User["id"] | null,
) => DsmQueryOptions<UserDataResponse, "enabled"> {
  const { userApi } = useDsmContext();

  return (userId) => {
    const enabled = userId != null;

    return {
      queryKey: userKeys.fetch(userId),
      queryFn({ signal }) {
        invariant(enabled, "User ID not provided");

        return userApi.fetchUser({ userId }, { signal });
      },
      enabled,
      context: DsmQueryClientContext,
    };
  };
}

export function useUser<TData = UserDataResponse>(
  userId: User["id"] | null,
  options?: Pick<UseQueryOptions<UserDataResponse, unknown, TData>, "select">,
): UseQueryResult<TData> {
  const createUserQueryOptions = useUserQueryOptionsFactory();

  return useQuery({
    ...options,
    ...createUserQueryOptions(userId),
  });
}

export function useUserCreateMutationOptionsFactory(): () => DsmMutationOptions<
  UserDataResponse,
  UserCreateRequest,
  "onSuccess"
> {
  const { userApi } = useDsmContext();

  const queryClient = useDsmQueryClient();

  return () => ({
    mutationFn(request) {
      return userApi.createUser({ userCreateRequest: request });
    },
    onSuccess(response) {
      queryClient.removeQueries({ queryKey: userKeys.lists() });
      queryClient.setQueryData(userKeys.fetch(response.data.id), response);
    },
    context: DsmQueryClientContext,
  });
}

export function useUserUpdateMutationOptionsFactory(): (
  userId: Maybe<User["id"]>,
) => DsmMutationOptions<UserDataResponse, UserUpdateRequest, "onSuccess"> {
  const { userApi } = useDsmContext();

  const queryClient = useDsmQueryClient();

  return (userId) => {
    const enabled = userId != null;

    return {
      mutationFn(request) {
        invariant(enabled, "Mutation not enabled");

        return userApi.updateUser({ userId, userUpdateRequest: request });
      },
      onSuccess(response) {
        queryClient.removeQueries({ queryKey: userKeys.lists() });
        queryClient.setQueryData(userKeys.fetch(response.data.id), response);
      },
      context: DsmQueryClientContext,
    };
  };
}

export function useUserDeleteMutationOptionsFactory(): (
  userId: User["id"],
) => DsmMutationOptions<void, void> {
  const { userApi } = useDsmContext();

  const queryClient = useDsmQueryClient();

  return (userId) => ({
    mutationFn() {
      return userApi.deleteUser({ userId });
    },
    onSuccess() {
      queryClient.removeQueries({ queryKey: userKeys.lists() });
      scheduleQueriesRemoval(() =>
        queryClient.removeQueries({ queryKey: userKeys.fetch(userId) }),
      );
    },
    context: DsmQueryClientContext,
  });
}

export function useCurrentUserQueryOptionsFactory(): () => DsmQueryOptions<MeDataResponse> {
  const { userApi } = useDsmContext();

  return () => ({
    queryKey: userKeys.me(),
    queryFn({ signal }) {
      return userApi.fetchUserMe({ signal });
    },
    context: DsmQueryClientContext,
  });
}

export function useCurrentDsmUser<TData = MeDataResponse>(
  options?: Pick<UseQueryOptions<MeDataResponse, unknown, TData>, "select">,
): UseQueryResult<TData> {
  const currentUserQueryOptionsFactory = useCurrentUserQueryOptionsFactory();

  return useQuery({
    ...currentUserQueryOptionsFactory(),
    ...options,
  });
}

export function useUpdateCurrentDsmUser(): UseMutationResult<
  UserDataResponse,
  unknown,
  UserUpdateRequest
> {
  const { userApi } = useDsmContext();

  const queryClient = useDsmQueryClient();

  const currentDsmUserQuery = useCurrentDsmUser();

  const currentUserId = currentDsmUserQuery.data?.data?.id;
  const enabled = currentUserId != null;

  return useDsmMutation({
    async mutationFn(request) {
      invariant(enabled, "Current DSM user not available");

      let finalRequest = request;
      // If a `context` field was passed it needs to be merged in with the
      // user resource's existing `context`, if any.
      if (request.context != null) {
        // The cached user data could've been fetched a while ago, so refetch
        // the data so we're merging with as recent of `context` as we can
        // reasonably do.
        const freshResponse = await currentDsmUserQuery.refetch({
          throwOnError: true,
        });

        invariant(freshResponse.isSuccess, "Expected successful response");

        const {
          data: { data: freshUser },
        } = freshResponse;

        invariant(freshUser != null, "Expected a user");

        finalRequest = {
          ...finalRequest,
          context: merge(
            // Since we're merging `freshUser.context` which isn't completely
            // under Studio's control, there's a risk of prototype pollution.
            // Lodash's `merge` function should prevent that but as a precaution
            // go ahead and use a null-prototype object as the merge target.
            Object.create(null),
            freshUser.context,
            request.context,
          ),
        };
      }

      return userApi.updateUser({
        userId: currentUserId,
        userUpdateRequest: finalRequest,
      });
    },
    onSuccess(response) {
      queryClient.removeQueries({
        queryKey: userKeys.lists(),
        type: "inactive",
      });
      queryClient.setQueryData(userKeys.fetch(response.data.id), response);
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: userKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: userKeys.me() }),
      ]);
    },
  });
}
